// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <https://benrichardson.dev>
// Based on QuakeWatch by Ben Richardson — https://benrichardson.dev
//
// Point-in-polygon classification of an epicentre to its tectonic plate.
// Pure geometry — unit-tested with synthetic polygons and no DOM.

export interface PlateFeature {
  code: string;
  name: string;
  /** GeoJSON polygon rings: outer ring first; [lon,lat] pairs. */
  rings: number[][][];
}

/** Ray-casting test: is [lon,lat] inside a single ring? */
export function pointInRing(lon: number, lat: number, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const intersect = yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Inside the outer ring (rings[0]) — plate polygons here have no holes. */
export function pointInPolygon(lon: number, lat: number, rings: number[][][]): boolean {
  if (rings.length === 0) return false;
  return pointInRing(lon, lat, rings[0]);
}

/** Parse the PB2002 plates GeoJSON into a flat classifier list. */
export function parsePlates(geojson: any): PlateFeature[] {
  const out: PlateFeature[] = [];
  const feats = geojson?.features ?? [];
  for (const f of feats) {
    const code = f?.properties?.Code ?? f?.properties?.code ?? '';
    const name = f?.properties?.PlateName ?? f?.properties?.plateName ?? code;
    const geom = f?.geometry;
    if (!geom) continue;
    if (geom.type === 'Polygon') {
      out.push({ code, name, rings: geom.coordinates });
    } else if (geom.type === 'MultiPolygon') {
      // Represent each polygon of a multipolygon separately, same plate identity.
      for (const poly of geom.coordinates) out.push({ code, name, rings: poly });
    }
  }
  return out;
}

/**
 * Classify a coordinate to its tectonic plate name; returns '' if no plate
 * polygon contains the point (rare — usually a coordinate wrap issue).
 */
export function classifyPlate(lat: number, lon: number, plates: PlateFeature[]): string {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return '';
  for (const p of plates) {
    if (pointInPolygon(lon, lat, p.rings)) return p.name;
  }
  // Try the antimeridian-shifted longitude in case of dateline wrapping.
  const alt = lon > 0 ? lon - 360 : lon + 360;
  for (const p of plates) {
    if (pointInPolygon(alt, lat, p.rings)) return p.name;
  }
  return '';
}
